/**
 * Aamra Navigation Controller
 * Handles:
 * - Desktop category bar hover/click mega menu panel transitions
 * - Mobile drawer open/close and toggle triggers
 * - Mobile drawer Level 1 -> Level 2 sliding drill-down
 * - Mobile Level 2 back button navigation
 * - State reset on drawer close
 */
(function () {
  'use strict';

  function getMobileNavStore() {
    try {
      if (window.Alpine && typeof Alpine.store === 'function') {
        return Alpine.store('xMobileNav');
      }
    } catch (e) {
      // Store not registered yet
    }
    return null;
  }

  function initAamraNavigation() {
    initDesktopMegaMenu();
    initMobileDrawer();
  }

  /* -------------------------------------------------------------------------
     Desktop Category Bar & Mega Menu
     ------------------------------------------------------------------------- */
  function initDesktopMegaMenu() {
    const wrapper = document.querySelector('.aamra-desktop-nav-wrapper');
    if (!wrapper) return;

    const catItems = wrapper.querySelectorAll('.aamra-desktop-cat-item');
    const megaPanels = wrapper.querySelectorAll('.aamra-desktop-mega-panel');
    let closeTimeout = null;

    function closeAllPanels() {
      megaPanels.forEach(p => p.classList.remove('is-open'));
      catItems.forEach(i => i.classList.remove('is-hovered'));
      wrapper.classList.remove('has-active-mega');
    }

    catItems.forEach(item => {
      const catKey = item.getAttribute('data-desktop-cat');
      const targetPanel = wrapper.querySelector(`.aamra-desktop-mega-panel[data-mega-cat="${catKey}"]`);

      const openThisItem = () => {
        clearTimeout(closeTimeout);

        // Highlight this item and dim others
        catItems.forEach(i => {
          if (i !== item) i.classList.remove('is-hovered');
        });
        item.classList.add('is-hovered');

        if (targetPanel) {
          megaPanels.forEach(p => {
            if (p !== targetPanel) p.classList.remove('is-open');
          });
          targetPanel.classList.add('is-open');
          wrapper.classList.add('has-active-mega');
        } else {
          megaPanels.forEach(p => p.classList.remove('is-open'));
          wrapper.classList.remove('has-active-mega');
        }
      };

      const scheduleClose = () => {
        closeTimeout = setTimeout(() => {
          closeAllPanels();
        }, 180);
      };

      item.addEventListener('mouseenter', openThisItem);
      item.addEventListener('mouseleave', scheduleClose);

      if (targetPanel) {
        targetPanel.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          wrapper.classList.add('has-active-mega');
          item.classList.add('is-hovered');
        });
        targetPanel.addEventListener('mouseleave', scheduleClose);
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        closeAllPanels();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAllPanels();
      }
    });
  }

  /* -------------------------------------------------------------------------
     Mobile Navigation Drawer (Open / Close, Level 1 & Level 2 Drill-down)
     ------------------------------------------------------------------------- */
  function initMobileDrawer() {
    function getDrawer() {
      return document.querySelector('.aamra-mobile-nav-root');
    }

    function goToLevel1() {
      const drawer = getDrawer();
      if (!drawer) return;
      drawer.classList.remove('is-subnav-active');
      const level1 = drawer.querySelector('.aamra-mobile-level-1');
      const level2 = drawer.querySelector('.aamra-mobile-level-2');
      if (level2) level2.classList.remove('is-active');
      if (level1) level1.classList.remove('is-hidden');
    }

    function goToLevel2(catKey, catName) {
      const drawer = getDrawer();
      if (!drawer) return;
      drawer.classList.add('is-subnav-active');
      const level1 = drawer.querySelector('.aamra-mobile-level-1');
      const level2 = drawer.querySelector('.aamra-mobile-level-2');
      const categoryTitle = drawer.querySelector('.aamra-mobile-level-2-title');
      const catContents = drawer.querySelectorAll('.aamra-mobile-cat-content');

      if (!level1 || !level2) return;

      catContents.forEach(content => {
        if (content.getAttribute('data-category') === catKey) {
          content.style.display = 'block';
        } else {
          content.style.display = 'none';
        }
      });

      if (categoryTitle) {
        categoryTitle.textContent = (catName || catKey).toUpperCase();
      }

      level1.classList.add('is-hidden');
      level2.classList.add('is-active');
      level2.scrollTop = 0;
    }

    // Delegated click handler on document for ALL mobile drawer triggers & navigation
    document.addEventListener('click', function (e) {
      // 1. Toggle Hamburger Menu (Open)
      const hamburger = e.target.closest('#mobile-navigation:not(.menu-drawer), .mobile-navigation-modern-icon-bg, [aria-label="Menu"]');
      if (hamburger) {
        e.preventDefault();
        const store = getMobileNavStore();
        if (store && typeof store.open === 'function') {
          store.open();
        } else {
          // Direct DOM fallback
          const drawerContainer = document.querySelector('#mobile-navigation.menu-drawer');
          if (drawerContainer) drawerContainer.classList.add('menu-opening');
          const backdrop = document.querySelector('.section-mobile-navigation .opacity-60.bg-black');
          if (backdrop) backdrop.style.display = 'block';
        }
        return;
      }

      // 2. Close Drawer button (X)
      const closeBtn = e.target.closest('.aamra-mobile-close-btn');
      if (closeBtn) {
        e.preventDefault();
        const store = getMobileNavStore();
        if (store && typeof store.close === 'function') {
          store.close();
        } else {
          const drawerContainer = document.querySelector('#mobile-navigation.menu-drawer');
          if (drawerContainer) drawerContainer.classList.remove('menu-opening');
          const backdrop = document.querySelector('.section-mobile-navigation .opacity-60.bg-black');
          if (backdrop) backdrop.style.display = 'none';
        }
        setTimeout(goToLevel1, 300);
        return;
      }

      // 2b. Close Drawer on Backdrop click
      const backdropEl = e.target.closest('.section-mobile-navigation .opacity-60.bg-black');
      if (backdropEl) {
        e.preventDefault();
        const store = getMobileNavStore();
        if (store && typeof store.close === 'function') {
          store.close();
        } else {
          const drawerContainer = document.querySelector('#mobile-navigation.menu-drawer');
          if (drawerContainer) drawerContainer.classList.remove('menu-opening');
          backdropEl.style.display = 'none';
        }
        setTimeout(goToLevel1, 300);
        return;
      }

      // 3. Level 1 -> Level 2 Category Drill-down click
      const catTrigger = e.target.closest('[data-aamra-category-trigger]');
      if (catTrigger) {
        e.preventDefault();
        const catKey = catTrigger.getAttribute('data-aamra-category-trigger');
        const catName = catTrigger.getAttribute('data-aamra-category-name');
        goToLevel2(catKey, catName);
        return;
      }

      // 4. Level 2 Back button click
      const backBtn = e.target.closest('.aamra-mobile-back-btn');
      if (backBtn) {
        e.preventDefault();
        goToLevel1();
        return;
      }
    });

    // Safely patch Alpine store so closing the drawer resets drilldown to Level 1
    function patchAlpineStore() {
      const store = getMobileNavStore();
      if (store && !store._aamraPatched && typeof store.close === 'function') {
        const originalClose = store.close;
        store.close = function () {
          originalClose.apply(this, arguments);
          setTimeout(goToLevel1, 300);
        };
        store._aamraPatched = true;
      }
    }

    patchAlpineStore();
    document.addEventListener('alpine:init', patchAlpineStore);
    document.addEventListener('alpine:initialized', patchAlpineStore);
  }

  // Auto-init on DOMContentLoaded or immediate if already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAamraNavigation);
  } else {
    initAamraNavigation();
  }

  // Re-init on Shopify Section Load in Theme Editor
  document.addEventListener('shopify:section:load', initAamraNavigation);
})();
